/**
 * <dealer-map> — the dealer finder: a searchable list beside a map of the same dealers.
 *
 * Light DOM, progressive enhancement. The list and the map are built from one JSON file
 * (data-src): [{ id, name, city, postcode, lat, lng, phone, country }]. Everything the visitor
 * reads is built with textContent, so dealer data can never inject markup.
 *
 * Markup contract (sections/dealer-map.liquid):
 *   [data-tabs]    country buttons, shown only when the data holds more than one country
 *   [data-search]  text input: name, city or postcode
 *   [data-count]   "N stores"; data-count = other-form with %COUNT%, data-count-one = one-form
 *   [data-list]    the dealers, one <li> each
 *   [data-map]     the drawing surface
 *   [data-card]    the selected dealer, under the map
 *   [data-empty]   shown when a search matches nothing
 *
 * The map is plain SVG: dealers are plotted from their coordinates, with pan (drag) and zoom
 * (wheel and buttons).
 * Filters: [data-filter="shop"] and [data-filter="workshop"] checkboxes narrow the list. Without
 * a key it needs no third-party script. With data-maps-key (the theme setting
 * google_maps_api_key) the SVG is swapped for a Google map once the surface scrolls into view;
 * a rejected key (gm_authFailure) or a failed script brings the SVG back. Only drawMap() differs
 * between the two; the list, search and selection do not care what draws the dots.
 */
(function () {
  if (customElements.get('dealer-map')) return;

  const NS = 'http://www.w3.org/2000/svg';
  const PAGE = 30;
  let googleLoad = null;

  function loadGoogle(key, lang) {
    if (googleLoad) return googleLoad;
    googleLoad = new Promise((resolve, reject) => {
      window.__dealerMapGoogleReady = resolve;
      const script = document.createElement('script');
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) +
        '&v=weekly&loading=async&callback=__dealerMapGoogleReady' + (lang ? '&language=' + encodeURIComponent(lang) : '');
      script.async = true;
      script.onerror = () => { googleLoad = null; reject(new Error('Google Maps failed to load')); };
      document.head.appendChild(script);
    });
    return googleLoad;
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function directionsUrl(store) {
    return 'https://www.google.com/maps/dir/?api=1&destination=' + store.lat + ',' + store.lng;
  }

  class DealerMap extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.stores = [];
      this.country = '';
      this.query = '';
      this.selected = null;
      this.view = null;
      this.pins = [];
      this.origin = null;
      // Sorted the way the page's language sorts: Å, Ä and Ö after Z in Swedish, not with A.
      this.collator = new Intl.Collator(this.dataset.lang || document.documentElement.lang || undefined);
      this.nearEl = this.querySelector('[data-near]');
      this.nearMsg = this.querySelector('[data-near-msg]');

      this.tabsEl = this.querySelector('[data-tabs]');
      this.searchEl = this.querySelector('[data-search]');
      this.countEl = this.querySelector('[data-count]');
      this.listEl = this.querySelector('[data-list]');
      this.mapEl = this.querySelector('[data-map]');
      this.cardEl = this.querySelector('[data-card]');
      this.emptyEl = this.querySelector('[data-empty]');
      this.errorEl = this.querySelector('[data-error]');

      if (this.searchEl) {
        this.searchEl.addEventListener('input', () => {
          this.query = this.searchEl.value.trim().toLowerCase().replace(/\s+/g, ' ');
          this.selected = null;
          this.render(true);
        });
      }
      this.querySelectorAll('[data-zoom]').forEach((button) => {
        button.addEventListener('click', () => this.zoom(Number(button.dataset.zoom)));
      });
      this.querySelectorAll('[data-filter]').forEach((box) => {
        box.addEventListener('change', () => { this.selected = null; this.render(true); });
      });
      this.querySelector('[data-reset]')?.addEventListener('click', () => this.render(true));
      if (this.nearEl) this.nearEl.addEventListener('click', () => this.nearMe());

      this.load();
    }

    async load() {
      try {
        const response = await fetch(this.dataset.src);
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const data = await response.json();
        this.stores = Array.isArray(data) ? data.filter((s) => s && s.name && isFinite(s.lat) && isFinite(s.lng)) : [];
      } catch (error) {
        if (this.errorEl) this.errorEl.hidden = false;
        return;
      }
      const countries = [];
      this.stores.forEach((s) => { if (countries.indexOf(s.country) === -1) countries.push(s.country); });
      // Markets in the order Valeryd sells in them, not the order the rows arrive in.
      const order = ['SE', 'NO', 'DK', 'FI', 'DE', 'AT', 'FR', 'HR'];
      countries.sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99));
      const wanted = String(this.dataset.defaultCountry || '').toUpperCase();
      this.country = countries.indexOf(wanted) !== -1 ? wanted : countries[0] || '';
      this.buildTabs(countries);
      if (this.stores.some((s) => s.tier)) {
        const legend = document.querySelector('[data-tiers]');
        if (legend) legend.hidden = false;
        const filter = this.querySelector('[data-tier-filter]');
        if (filter) filter.hidden = false;
      }
      this.render(true);
      this.startGoogle();
    }

    // Google Maps sends the visitor's address to Google, so it loads only with Cookiebot's
    // preferences consent, as the old dealer page did. Until then the plain map stays, with a note
    // and a button that gives that one consent; the list, search and filters never wait for it.
    consented() {
      const consent = window.Cookiebot && window.Cookiebot.consent;
      return !!(consent && consent.preferences);
    }

    startGoogle() {
      const key = this.dataset.mapsKey;
      if (!key) return;
      if (!this.consented()) { this.askConsent(key); return; }
      let started = false;
      const begin = () => {
        if (started) return;
        started = true;
        this.initGoogle(key).catch(() => this.fallback());
      };
      if (!('IntersectionObserver' in window)) { begin(); return; }
      const watcher = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { watcher.disconnect(); begin(); }
      });
      watcher.observe(this.mapEl);
      // An observer can stay silent in a hidden or embedded view; do not leave the visitor on the plain map.
      setTimeout(begin, 2500);
    }

    askConsent(key) {
      const note = this.querySelector('[data-consent]');
      const button = this.querySelector('[data-consent-accept]');
      if (note) note.hidden = false;
      const cookiebot = window.Cookiebot;
      if (button && cookiebot && (cookiebot.submitCustomConsent || cookiebot.renew)) {
        button.hidden = false;
        button.addEventListener('click', () => {
          const c = cookiebot.consent || {};
          if (cookiebot.submitCustomConsent) cookiebot.submitCustomConsent(true, !!c.statistics, !!c.marketing);
          else cookiebot.renew();
        });
      }
      this.onConsent = () => {
        if (!this.consented()) return;
        window.removeEventListener('CookiebotOnAccept', this.onConsent);
        if (note) note.hidden = true;
        this.startGoogle();
      };
      window.addEventListener('CookiebotOnAccept', this.onConsent);
    }

    disconnectedCallback() {
      if (this.onConsent) window.removeEventListener('CookiebotOnAccept', this.onConsent);
    }

    async initGoogle(key) {
      window.gm_authFailure = () => this.fallback();
      await loadGoogle(key, this.dataset.lang);
      const maps = await google.maps.importLibrary('maps');
      const marker = await google.maps.importLibrary('marker');
      this.GMarker = marker.Marker;
      this.mapEl.textContent = '';
      this.mapEl.classList.add('is-google');
      const canvas = el('div');
      canvas.style.cssText = 'inline-size:100%;block-size:100%';
      this.mapEl.appendChild(canvas);
      this.gmap = new maps.Map(canvas, {
        center: { lat: 62, lng: 15 },
        zoom: 5,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        gestureHandling: 'cooperative',
        // Quiet base map so the dealers are what stands out: no shops, transit or road icons.
        styles: [
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
          { stylers: [{ saturation: -35 }] },
        ],
      });
      this.pins = [];
      this.gmap.addListener('idle', () => this.paintGoogle());
      const controls = this.querySelector('.dealer-map__controls');
      if (controls) controls.style.display = 'none';
      this.drawMap(true);
    }

    // The key was rejected or the script never arrived: show the plain map again.
    fallback() {
      this.pins.forEach((p) => p.setMap(null));
      this.pins = [];
      this.gmap = null;
      this.mapEl.classList.remove('is-google');
      this.mapEl.textContent = '';
      const controls = this.querySelector('.dealer-map__controls');
      if (controls) controls.style.display = '';
      if (!this.stores.length) return;
      this.fit();
      this.drawMap();
    }

    buildTabs(countries) {
      if (!this.tabsEl || countries.length < 2) return;
      const names = this.dataset.countryNames ? JSON.parse(this.dataset.countryNames) : {};
      this.tabsEl.hidden = false;
      countries.forEach((code) => {
        const button = el('button', 'dealer-map__tab', names[code] || code);
        button.type = 'button';
        button.dataset.country = code;
        button.addEventListener('click', () => {
          this.country = code;
          this.selected = null;
          this.render(true);
        });
        this.tabsEl.appendChild(button);
      });
    }

    // Straight-line distance in km from where the visitor is (only once they asked for it).
    km(store) {
      if (!this.origin) return null;
      const rad = Math.PI / 180;
      const dLat = (store.lat - this.origin.lat) * rad;
      const dLng = (store.lng - this.origin.lng) * rad;
      const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(this.origin.lat * rad) * Math.cos(store.lat * rad) * Math.sin(dLng / 2) ** 2;
      return 6371 * 2 * Math.asin(Math.sqrt(a));
    }

    kmText(store) {
      const d = this.km(store);
      if (d == null) return '';
      return (d < 10 ? d.toFixed(1) : String(Math.round(d))) + ' km';
    }

    // Asks the browser for the position once, on click; nothing is requested on page load.
    nearMe() {
      if (this.origin) {
        this.origin = null;
        this.selected = null;
        this.nearEl.setAttribute('aria-pressed', 'false');
        this.nearEl.textContent = this.dataset.nearLabel || this.nearEl.textContent;
        this.render(true);
        return;
      }
      const fail = () => {
        if (this.nearMsg) { this.nearMsg.textContent = this.dataset.nearError || ''; this.nearMsg.hidden = false; }
      };
      if (!navigator.geolocation) { fail(); return; }
      navigator.geolocation.getCurrentPosition((pos) => {
        if (this.nearMsg) this.nearMsg.hidden = true;
        this.origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        let best = null;
        this.stores.forEach((s) => { if (!best || this.km(s) < this.km(best)) best = s; });
        if (best) this.country = best.country;
        this.query = '';
        if (this.searchEl) this.searchEl.value = '';
        this.selected = best ? best.id : null;
        this.nearEl.setAttribute('aria-pressed', 'true');
        this.nearEl.dataset.label = this.nearEl.textContent;
        this.nearEl.textContent = this.dataset.nearClear || this.nearEl.textContent;
        this.render(true);
        const row = best && this.rows && this.rows[best.id];
        if (row) row.scrollIntoView({ block: 'nearest' });
      }, fail, { timeout: 10000, maximumAge: 300000 });
    }

    // What the map frames: everything shown, or the closest few once we know where the visitor is.
    focusSet() {
      if (!this.origin || !this.visible.length) return this.visible;
      return this.visible.slice().sort((a, b) => this.km(a) - this.km(b)).slice(0, 6).concat([this.origin]);
    }

    flag(name) {
      const box = this.querySelector('[data-filter="' + name + '"]');
      return !!(box && box.checked);
    }

    matches() {
      const q = this.query;
      const compact = q.replace(/\s/g, '');
      // The filters add up, as on the old dealer page: each one ticked brings its dealers in, so all
      // three show everyone. None ticked shows everyone too rather than an empty map.
      const tier = this.flag('tier'), shop = this.flag('shop'), workshop = this.flag('workshop');
      const any = tier || shop || workshop;
      return this.stores.filter((s) => {
        if (s.country !== this.country) return false;
        if (any && !((tier && s.tier) || (shop && s.shop) || (workshop && s.workshop))) return false;
        if (!q) return true;
        return (
          s.name.toLowerCase().indexOf(q) !== -1 ||
          String(s.city || '').toLowerCase().indexOf(q) !== -1 ||
          String(s.postcode || '').replace(/\s/g, '').toLowerCase().indexOf(compact) !== -1
        );
      });
    }

    render(refit) {
      this.visible = this.matches();
      if (this.tabsEl) {
        this.tabsEl.querySelectorAll('button').forEach((b) => {
          b.setAttribute('aria-pressed', String(b.dataset.country === this.country));
        });
      }
      this.renderCount();
      this.renderList();
      if (this.emptyEl) this.emptyEl.hidden = this.visible.length > 0;
      if (refit) this.fit();
      this.drawMap(refit);
      this.renderCard();
    }

    renderCount() {
      if (!this.countEl) return;
      const n = this.visible.length;
      const template = n === 1 ? this.countEl.dataset.countOne : this.countEl.dataset.count;
      // "378 återförsäljare i Sverige": the country phrase is its own string, since languages inflect it.
      const where = this.dataset.inCountry ? JSON.parse(this.dataset.inCountry)[this.country] : '';
      // The number on its own, so it can carry the weight; the words around it stay in the language's order.
      const [before, after] = String(template || '%COUNT%').split('%COUNT%');
      this.countEl.textContent = '';
      if (before && before.trim()) this.countEl.appendChild(el('span', '', before.trim()));
      this.countEl.appendChild(el('strong', 'dealer-map__count-n', String(n)));
      const rest = [String(after || '').trim(), where].filter(Boolean).join(' ');
      if (rest) this.countEl.appendChild(el('span', '', rest));
    }

    renderList() {
      this.listEl.textContent = '';
      this.rows = {};
      const sorted = this.visible.slice().sort((a, b) => this.origin
        ? this.km(a) - this.km(b)
        : this.collator.compare(String(a.city), String(b.city)) || this.collator.compare(a.name, b.name));
      sorted.forEach((store) => {
        const li = el('li', 'dealer-map__item');
        const pick = el('button', 'dealer-map__pick');
        pick.type = 'button';
        pick.appendChild(el('span', 'dealer-map__name', store.name));
        if (this.origin) pick.appendChild(el('span', 'dealer-map__distance', this.kmText(store)));
        if (store.address) pick.appendChild(el('span', 'dealer-map__place', store.address));
        pick.appendChild(el('span', 'dealer-map__place', [store.postcode, this.title(store.city)].filter(Boolean).join(' ')));
        const kinds = [store.shop && this.dataset.labelShop, store.workshop && this.dataset.labelWorkshop].filter(Boolean);
        if (kinds.length) pick.appendChild(el('span', 'dealer-map__kinds', kinds.join(' · ')));
        const emblem = store.tier && this.dataset['tier' + this.cap(store.tier) + 'Img'];
        if (emblem) {
          const img = el('img', 'dealer-map__emblem');
          img.src = emblem;
          img.alt = this.dataset['tier' + this.cap(store.tier)] || '';
          img.width = 52;
          img.height = 52;
          img.loading = 'lazy';
          pick.appendChild(img);
        }
        pick.addEventListener('click', () => this.select(store.id));
        li.appendChild(pick);
        const actions = el('p', 'dealer-map__actions');
        if (store.phone) actions.appendChild(this.callLink(store));
        actions.appendChild(this.routeLink(store, 'dealer-map__link dealer-map__link--route'));
        li.appendChild(actions);
        this.rows[store.id] = li;
        if (sorted.indexOf(store) >= PAGE) li.classList.add('is-more');
        this.listEl.appendChild(li);
      });
      // On a phone the list shows PAGE dealers and a button for the rest (CSS does the hiding).
      this.listEl.classList.toggle('is-open', !!this.listOpen);
      if (sorted.length > PAGE) {
        const row = el('li', 'dealer-map__more-row');
        const more = el('button', 'dealer-map__more', String(this.dataset.showAll || '%COUNT%').replace('%COUNT%', String(sorted.length)));
        more.type = 'button';
        more.addEventListener('click', () => { this.listOpen = true; this.listEl.classList.add('is-open'); });
        row.appendChild(more);
        this.listEl.appendChild(row);
      }
      this.markSelected();
    }

    title(city) {
      return String(city || '').toLowerCase().replace(/(^|[\s-])\S/g, (m) => m.toUpperCase());
    }

    renderCard() {
      if (!this.cardEl) return;
      this.cardEl.textContent = '';
      const store = this.stores.find((s) => s.id === this.selected);
      this.cardEl.hidden = !store;
      if (!store) return;
      this.cardEl.appendChild(el('strong', 'dealer-map__name', store.name));
      if (store.address) this.cardEl.appendChild(el('span', 'dealer-map__place', store.address));
      this.cardEl.appendChild(el('span', 'dealer-map__place', [store.postcode, this.title(store.city)].filter(Boolean).join(' ')));
      if (this.origin) this.cardEl.appendChild(el('span', 'dealer-map__distance', this.kmText(store)));
      if (store.tier && this.dataset['tier' + this.cap(store.tier) + 'Img']) {
        const tier = el('p', 'dealer-map__tier');
        const img = el('img');
        img.src = this.dataset['tier' + this.cap(store.tier) + 'Img'];
        img.alt = '';
        img.width = 52;
        img.height = 52;
        tier.appendChild(img);
        tier.appendChild(el('span', '', this.dataset['tier' + this.cap(store.tier)] || ''));
        this.cardEl.appendChild(tier);
      }
      const kinds = [];
      if (store.shop && this.dataset.labelShop) kinds.push(this.dataset.labelShop);
      if (store.workshop && this.dataset.labelWorkshop) kinds.push(this.dataset.labelWorkshop);
      if (kinds.length) {
        const badges = el('ul', 'dealer-map__badges');
        badges.setAttribute('role', 'list');
        kinds.forEach((k) => badges.appendChild(el('li', 'dealer-map__badge', k)));
        this.cardEl.appendChild(badges);
      }
      const actions = el('div', 'dealer-map__card-actions');
      if (store.phone) actions.appendChild(this.callLink(store));
      actions.appendChild(this.routeLink(store, 'dealer-map__route'));
      this.cardEl.appendChild(actions);
    }

    // An icon from the theme's icon set, rendered once into a <template> by the section.
    icon(name) {
      const tpl = this.querySelector('template[data-icon="' + name + '"]');
      return tpl ? tpl.content.cloneNode(true) : document.createTextNode('');
    }

    callLink(store) {
      const call = el('a', 'dealer-map__link dealer-map__link--phone');
      call.href = 'tel:' + store.phone.replace(/[^\d+]/g, '');
      call.appendChild(this.icon('phone'));
      call.appendChild(el('span', '', store.phone));
      return call;
    }

    routeLink(store, className) {
      const route = el('a', className);
      route.href = directionsUrl(store);
      route.target = '_blank';
      route.rel = 'noopener';
      route.appendChild(this.icon('map-pin'));
      route.appendChild(el('span', '', this.dataset.directionsLabel || 'Directions'));
      return route;
    }

    select(id) {
      this.selected = id;
      this.markSelected();
      this.renderCard();
      this.drawMap();
      const row = this.rows && this.rows[id];
      if (row && row.classList.contains('is-more')) { this.listOpen = true; this.listEl.classList.add('is-open'); }
      if (row) row.scrollIntoView({ block: 'nearest' });
    }

    markSelected() {
      Object.keys(this.rows || {}).forEach((id) => {
        this.rows[id].classList.toggle('is-selected', Number(id) === this.selected);
      });
    }

    /* ---- the map ---- */

    // Longitude is squeezed by cos(latitude) so distances look right at this latitude.
    project(store, k) {
      return { x: store.lng * k, y: -store.lat };
    }

    fit() {
      const set = this.visible.length ? this.focusSet() : this.stores.filter((s) => s.country === this.country);
      if (!set.length) { this.view = { x: 0, y: 0, w: 10, h: 10 }; return; }
      const mid = set.reduce((n, s) => n + s.lat, 0) / set.length;
      this.k = Math.cos((mid * Math.PI) / 180);
      const pts = set.map((s) => this.project(s, this.k));
      const xs = pts.map((p) => p.x);
      const ys = pts.map((p) => p.y);
      let w = Math.max(Math.max.apply(null, xs) - Math.min.apply(null, xs), 0.4);
      let h = Math.max(Math.max.apply(null, ys) - Math.min.apply(null, ys), 0.4);
      const pad = Math.max(w, h) * 0.08;
      const cx = (Math.max.apply(null, xs) + Math.min.apply(null, xs)) / 2;
      const cy = (Math.max.apply(null, ys) + Math.min.apply(null, ys)) / 2;
      w += pad * 2;
      h += pad * 2;
      // Match the surface's aspect so nothing is cropped.
      const aspect = (this.mapEl.clientWidth || 600) / (this.mapEl.clientHeight || 600);
      if (w / h > aspect) h = w / aspect; else w = h * aspect;
      this.view = { x: cx - w / 2, y: cy - h / 2, w: w, h: h };
      this.home = Object.assign({}, this.view);
    }

    cap(word) { return word.charAt(0).toUpperCase() + word.slice(1); }

    // A map pin as an inline SVG: blue with a white V for a dealer that stocks our parts, lighter
    // blue with a spanner for a workshop. The rim is the partner level: gold a gold ring, silver a thick
    // metallic ring, platinum a premium silver: a darker blue with a few broad facets, a bright ring and a faint
    // glow, so two near-identical colours are not what tells the levels apart. No symbols or patterns on any
    // pin. No level keeps a white rim. Drawn here, so no image request per dealer.
    pinIcon(store, picked) {
      const g = google.maps;
      const rims = { guld: '#d9a520' };
      const platinum = store.tier === 'platina';
      const workshop = store.workshop && !store.shop;
      const fill = platinum ? '#06264f' : workshop ? '#3d8fd6' : '#0b4a8f';
      const glyph = workshop
        ? '<g transform="translate(18 17) scale(0.62) translate(-12 -12)" fill="#fff"><path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/></g>'
        : '<polygon fill="#fff" points="' + [[230, 368], [507, 368], [625, 713], [748, 368], [1024, 368], [748, 953], [494, 953]]
          .map((pt) => (18 + (pt[0] - 627) * 0.0235).toFixed(2) + ',' + (19.5 + (pt[1] - 660) * 0.0235).toFixed(2)).join(' ') + '"/>';
      const body = 'M18 1.5C9 1.5 2 8.4 2 17.2c0 12 16 29.8 16 29.8s16-17.8 16-29.8C34 8.4 27 1.5 18 1.5z';
      let defs = '';
      let paint = '<path d="' + body + '" fill="' + fill + '" stroke="' + (rims[store.tier] || '#ffffff') + '" stroke-width="' + (store.tier ? 3.4 : 2.4) + '"/>';
      if (store.tier === 'silver') {
        defs = '<defs><linearGradient id="m" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#f4f6f8"/><stop offset="0.3" stop-color="#a3aab1"/>' +
          '<stop offset="0.55" stop-color="#eceff2"/><stop offset="0.8" stop-color="#868d95"/><stop offset="1" stop-color="#d7dbdf"/></linearGradient></defs>';
        paint = '<path d="' + body + '" fill="' + fill + '" stroke="url(#m)" stroke-width="5.9"/>';
      }
      if (platinum) {
        // A premium silver, quieter rather than busier: a darker blue body cut into three broad, barely
        // visible planes (a watch dial, brushed metal), a bright silver-white ring and a faint glow.
        defs = '<defs><linearGradient id="r" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#ffffff"/><stop offset="0.3" stop-color="#dfe9f4"/>' +
          '<stop offset="0.55" stop-color="#ffffff"/><stop offset="0.8" stop-color="#c8d6e6"/><stop offset="1" stop-color="#f3f7fc"/></linearGradient>' +
          '<filter id="o" x="-30%" y="-25%" width="160%" height="150%"><feGaussianBlur in="SourceAlpha" stdDeviation="1.2" result="b"/>' +
          '<feFlood flood-color="#dbe9fb" flood-opacity="0.55"/><feComposite in2="b" operator="in"/>' +
          '<feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter>' +
          '<clipPath id="c"><path d="' + body + '"/></clipPath></defs>';
        paint = '<path d="' + body + '" fill="' + fill + '" filter="url(#o)"/>' +
          '<g clip-path="url(#c)">' +
          '<polygon points="-4,-4 40,-4 40,4 -4,28" fill="#ffffff" fill-opacity="0.12"/>' +
          '<polygon points="-4,28 40,4 40,20 -4,44" fill="#ffffff" fill-opacity="0.042"/>' +
          '<polygon points="-4,44 40,20 40,52 -4,52" fill="#000000" fill-opacity="0.165"/></g>' +
          '<path d="' + body + '" fill="none" stroke="url(#r)" stroke-width="6.6"/>';
      }
      const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="44" height="54" viewBox="-4 -4 44 54">' + defs + paint + glyph + '</svg>';
      const k = (picked ? 1.3 : 1) * (platinum ? 1.05 : 1);
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
        scaledSize: new g.Size(44 * k, 54 * k),
        anchor: new g.Point(22 * k, 51 * k),
      };
    }

    drawGoogle(refit) {
      const g = google.maps;
      if (refit && this.visible.length) {
        const bounds = new g.LatLngBounds();
        this.focusSet().forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
        this.gmap.fitBounds(bounds, 48);
        g.event.addListenerOnce(this.gmap, 'idle', () => { if (this.gmap.getZoom() > 13) this.gmap.setZoom(13); });
      } else if (this.selected != null) {
        const store = this.visible.find((s) => s.id === this.selected);
        if (store) {
          this.gmap.panTo({ lat: store.lat, lng: store.lng });
          if (this.gmap.getZoom() < 10) this.gmap.setZoom(10);
        }
      }
      this.paintGoogle();
    }

    // Draws the dealers: single dots up close, numbered clusters when zoomed out. The chosen
    // dealer is always a single, larger dot; the visitor's own position is a blue dot.
    paintGoogle() {
      if (!this.gmap || !this.visible) return;
      const g = google.maps;
      const style = getComputedStyle(this);
      const base = style.getPropertyValue('--color-button').trim() || '#1b4f9c';
      this.pins.forEach((p) => p.setMap(null));
      this.pins = [];
      const add = (options) => {
        const pin = new this.GMarker(Object.assign({ map: this.gmap }, options));
        this.pins.push(pin);
        return pin;
      };
      const single = (s) => {
        const pin = add({ position: { lat: s.lat, lng: s.lng }, title: s.name, icon: this.pinIcon(s, false), zIndex: { platina: 5, guld: 4, silver: 3 }[s.tier] || 1 });
        pin.addListener('click', () => this.select(s.id));
      };
      const rest = this.visible.filter((s) => s.id !== this.selected);
      const zoom = this.gmap.getZoom();
      const projection = this.gmap.getProjection();
      if (!projection || zoom >= 9 || rest.length < 25) {
        rest.forEach(single);
      } else {
        const scale = Math.pow(2, zoom);
        const cell = 56;
        const groups = {};
        rest.forEach((s) => {
          const point = projection.fromLatLngToPoint(new g.LatLng(s.lat, s.lng));
          const key = Math.floor((point.x * scale) / cell) + ':' + Math.floor((point.y * scale) / cell);
          (groups[key] = groups[key] || []).push(s);
        });
        Object.keys(groups).forEach((key) => {
          const list = groups[key];
          if (list.length === 1) { single(list[0]); return; }
          const lat = list.reduce((n, s) => n + s.lat, 0) / list.length;
          const lng = list.reduce((n, s) => n + s.lng, 0) / list.length;
          const pin = add({
            position: { lat: lat, lng: lng },
            title: String(list.length),
            label: { text: String(list.length), color: '#ffffff', fontWeight: '700', fontSize: '13px' },
            icon: {
              path: g.SymbolPath.CIRCLE,
              scale: Math.min(26, 14 + Math.log2(list.length) * 3),
              fillColor: base,
              fillOpacity: 0.92,
              strokeColor: '#ffffff',
              strokeWeight: 3,
            },
            zIndex: 2,
          });
          pin.addListener('click', () => {
            const bounds = new g.LatLngBounds();
            list.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
            this.gmap.fitBounds(bounds, 60);
          });
        });
      }
      const picked = this.visible.find((s) => s.id === this.selected);
      if (picked) add({ position: { lat: picked.lat, lng: picked.lng }, title: picked.name, icon: this.pinIcon(picked, true), zIndex: 10 });
      if (this.origin) {
        add({
          position: { lat: this.origin.lat, lng: this.origin.lng },
          clickable: false,
          icon: { path: g.SymbolPath.CIRCLE, scale: 8, fillColor: '#1a73e8', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 3 },
          zIndex: 9,
        });
      }
    }

    drawMap(refit) {
      if (this.gmap) { this.drawGoogle(refit); return; }
      if (!this.view) return;
      let svg = this.mapEl.querySelector('svg');
      if (!svg) {
        svg = document.createElementNS(NS, 'svg');
        svg.setAttribute('role', 'img');
        svg.setAttribute('aria-label', this.dataset.mapLabel || 'Map of dealers');
        this.mapEl.appendChild(svg);
        this.bindPan(svg);
      }
      svg.setAttribute('viewBox', [this.view.x, this.view.y, this.view.w, this.view.h].join(' '));
      svg.textContent = '';
      const r = this.view.w / 90;
      const draw = (store, selected) => {
        const p = this.project(store, this.k);
        const dot = document.createElementNS(NS, 'circle');
        dot.setAttribute('cx', p.x);
        dot.setAttribute('cy', p.y);
        dot.setAttribute('r', selected ? r * 1.8 : r);
        dot.setAttribute('class', 'dealer-map__dot' + (selected ? ' is-selected' : ''));
        dot.setAttribute('stroke-width', r * 0.35);
        const label = document.createElementNS(NS, 'title');
        label.textContent = store.name;
        dot.appendChild(label);
        dot.addEventListener('click', (event) => { event.stopPropagation(); this.select(store.id); });
        svg.appendChild(dot);
      };
      this.visible.forEach((s) => { if (s.id !== this.selected) draw(s, false); });
      const chosen = this.visible.find((s) => s.id === this.selected);
      if (chosen) draw(chosen, true);
    }

    zoom(factor, anchor) {
      if (!this.view) return;
      const v = this.view;
      const ax = anchor ? anchor.x : v.x + v.w / 2;
      const ay = anchor ? anchor.y : v.y + v.h / 2;
      const f = factor > 0 ? 1 / 1.5 : 1.5;
      this.view = { x: ax - (ax - v.x) * f, y: ay - (ay - v.y) * f, w: v.w * f, h: v.h * f };
      this.drawMap();
    }

    bindPan(svg) {
      let drag = null;
      const unitsPerPx = () => this.view.w / (svg.clientWidth || 1);
      svg.addEventListener('pointerdown', (event) => {
        drag = { x: event.clientX, y: event.clientY, view: Object.assign({}, this.view), moved: false };
        svg.setPointerCapture(event.pointerId);
      });
      svg.addEventListener('pointermove', (event) => {
        if (!drag) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (Math.abs(dx) + Math.abs(dy) > 3) drag.moved = true;
        if (!drag.moved) return;
        const u = drag.view.w / (svg.clientWidth || 1);
        this.view = { x: drag.view.x - dx * u, y: drag.view.y - dy * u, w: drag.view.w, h: drag.view.h };
        svg.setAttribute('viewBox', [this.view.x, this.view.y, this.view.w, this.view.h].join(' '));
      });
      const end = () => { drag = null; };
      svg.addEventListener('pointerup', end);
      svg.addEventListener('pointercancel', end);
      svg.addEventListener('wheel', (event) => {
        event.preventDefault();
        const box = svg.getBoundingClientRect();
        const u = unitsPerPx();
        this.zoom(event.deltaY < 0 ? 1 : -1, {
          x: this.view.x + (event.clientX - box.left) * u,
          y: this.view.y + (event.clientY - box.top) * u,
        });
      }, { passive: false });
    }
  }

  customElements.define('dealer-map', DealerMap);
})();
